import {
    manageRouteCheckboxAssignments
} from '@containers/activity-router/routes/helpers/assignment/assignment-selected/route-assignments-selected';
import { isInstructionHubBlockedAssignment } from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub-helpers';
import { InstructionHubBlockedKeys } from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub.types';
import {
    getActivityKey,
    getStrandCourseKey,
    getTopicKey,
    parseStrandCourseKey
} from '@containers/activity-router/routes/helpers/keys/key-generator';
import { updateMultiSelectGridCellsAndCounters } from '@containers/activity-router/routes/helpers/multi-select/multi-select-helpers';
import { AssignmentsSelected } from '@containers/activity-router/routes/helpers/slice/slice.types';
import { GridCells, GridCounters } from '@containers/activity-router/routes/helpers/slice/topic/topic-slice.types';
import {
    ChallengesLevel
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/level-filter/level-filter.types';
import {
    ChallengesPlaylist, DEFAULT_PLAYLIST_ID, defaultPlaylistItem
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/playlist-filter/playlist-filter.types';
import {
    manageChallengesSelectedAssignments
} from '@containers/activity-router/routes/learn/challenges/helpers/slice/assignments/selected/challenges-assignments-selected-helpers';
import {
    ChallengesComplexity,
    TransformCourse,
    TransformedChallengesAssignments
} from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';

export type SelectCardViewItemsParams = {
    selectedItems: string[]
    gridCells: GridCells
    gridCounters: GridCounters
    assignmentsSelected: AssignmentsSelected
    assignmentsGrid: TransformedChallengesAssignments
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
};

function checkSelectionsOutsideGridPlaylist (iconViewActivityIds: Set<string>, playlistActivityIds: Set<string>): boolean {
    for (const val of iconViewActivityIds) {
        if (!playlistActivityIds.has(val)) return false;
    }

    return true;
}

function checkSelectionsWithinTopicFilter (selectedTopicIds: Set<string>, filterTopicSet: Set<string>): boolean {
    for (const topicId of selectedTopicIds) {
        if (!filterTopicSet.has(topicId)) return false;
    }

    return true;
}

export const generateGridFiltersFromCardView = ({
    course,
    selectedItems,
    assignmentsSelected,
    playlistGridFilter,
    filterStrand = [],
    filterTopic = []
}: {
    course: TransformCourse
    selectedItems: string[]
    assignmentsSelected: AssignmentsSelected
    playlistGridFilter: ChallengesPlaylist
    filterStrand?: string[]
    filterTopic?: string[]
}) => {
    const selectedKeySet = new Set(selectedItems);
    const previouslySelectedKeySet = new Set<string>();
    const dokSet = new Set<ChallengesComplexity>();
    const playlistSet = new Set<string>();
    const levelSet = new Set<ChallengesLevel>();
    const topicSet = new Set<string>();
    const strandSet = new Set<string>();
    const isItemSelected = (courseKey: string, activityKey: string) =>
        selectedKeySet.has(courseKey) || previouslySelectedKeySet.has(activityKey);

    // Create new set of ids to check prev grid selected items
    Object.values(assignmentsSelected).forEach((activities) => {
        Object.keys(activities).forEach((activityKey) => {
            previouslySelectedKeySet.add(activityKey);
        });
    });

    course.topics.forEach((strand) => {
        strand.topics?.forEach((topic) => {
            topic.activities.forEach((activity) => {
                const activityKey = getActivityKey({
                    id: activity.id,
                    activityType: activity.activityType
                });
                const courseKey = getStrandCourseKey({
                    strandId: strand.id,
                    topicId: topic.id,
                    activityId: activityKey
                });

                if (isItemSelected(courseKey, activityKey)) {
                    const {
                        extraProperties: { yearGroup },
                        complexity,
                        id
                    } = activity;

                    dokSet.add(complexity as ChallengesComplexity);
                    playlistSet.add(id);
                    levelSet.add(yearGroup);
                    topicSet.add(topic.id);
                    strandSet.add(strand.id);
                }
            });
        });
    });

    const selectionsConsistentWithPlaylist =
        playlistGridFilter.id !== DEFAULT_PLAYLIST_ID &&
        checkSelectionsOutsideGridPlaylist(playlistSet, new Set(playlistGridFilter.activityIds));
    const hasTopicFilter = filterTopic.length > 0;
    const selectionsWithinTopicFilter =
        hasTopicFilter &&
        checkSelectionsWithinTopicFilter(topicSet, new Set(filterTopic));

    return {
        updatedLevelFilter: Array.from(levelSet),
        updatedDokFilter: Array.from(dokSet),
        updatedPlaylistFilter: selectionsConsistentWithPlaylist ? playlistGridFilter : defaultPlaylistItem,
        updatedStrandFilter: selectionsWithinTopicFilter ? filterStrand : [],
        updatedTopicFilter: selectionsWithinTopicFilter ? filterTopic : []
    };
};

export const selectCardViewItems = ({
    selectedItems,
    gridCells,
    gridCounters,
    assignmentsSelected,
    assignmentsGrid,
    instructionHubBlockedKeys
}: SelectCardViewItemsParams) => {
    const isChecked = true;

    selectedItems.forEach((item) => {
        const {
            strandId,
            topicId,
            activityId
        } = parseStrandCourseKey(item);
        const topicKey = getTopicKey({
            strandId,
            topicId
        });
        const isSelectable = (studentKey: string) => !isInstructionHubBlockedAssignment({
            instructionHubBlockedKeys,
            studentKey,
            activityKey: activityId
        });

        Object.keys(gridCells).filter(isSelectable).forEach((studentId) => {
            updateMultiSelectGridCellsAndCounters({
                gridCells,
                gridCounters,
                studentId,
                topicId: topicKey,
                activityId
            });
        });
        assignmentsSelected = manageRouteCheckboxAssignments({
            selectedAssignments: assignmentsSelected,
            gridCells,
            isChecked,
            checkAssignmentEligibility: ({ studentKey, activityKey }) => activityKey === activityId && isSelectable(studentKey),
            manageSelectedAssignments: (params) => manageChallengesSelectedAssignments({
                ...params,
                studentAssignments: assignmentsGrid
            })
        });
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected
    };
};
